import { useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User } from '../types';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Fetch user data from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            setUser(userDoc.data() as User);
            setFirebaseUser(firebaseUser);
            setError(null);
          } else {
            // User authenticated but no Firestore document exists
            console.error('User document not found in Firestore');
            const errorMessage = `Account setup incomplete. Please create a Firestore document:\n\n` +
              `1. Go to Firestore Database in Firebase Console\n` +
              `2. Create collection: "users"\n` +
              `3. Document ID: ${firebaseUser.uid}\n` +
              `4. Add fields: uid, email, tier, createdAt\n\n` +
              `Your UID: ${firebaseUser.uid}`;
            setError(errorMessage);
            // Sign out to prevent confusion
            await firebaseSignOut(auth);
            setUser(null);
            setFirebaseUser(null);
          }
        } catch (err: any) {
          console.error('Error fetching user data:', err);
          setError(err.message);
        }
      } else {
        setUser(null);
        setFirebaseUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const signOut = async () => {
    setError(null);
    try {
      await firebaseSignOut(auth);
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  return {
    user,
    firebaseUser,
    loading,
    error,
    signIn,
    signOut,
  };
}
